<template>
  <div>
    <b-btn
      variant="secondary"
      size="sm"
      v-b-modal.manageDealsModal
      data-test="manage-deals-button"
      :disabled="isCampaignReadOnly || creatives.length === 0"
    >
      Manage deals
    </b-btn>

    <b-modal
      id="manageDealsModal"
      ref="manageDealsModal"
      title="Manage Publisher Deals"
      size="xl"
      ok-only
      ok-title="Save"
      ok-variant="primary"
      :ok-disabled="!hasDealListChanges || isLoading"
      @show="resetNewDeals"
      @hide="handleHide"
      @hidden="resetNewDeals"
      @ok="handleOk"
      data-test="manage-deals-modal"
    >
      <div
        v-if="isLoading"
        class="h-100 align-items-center d-flex justify-content-center card-body mb-0"
      >
        <div class="loader mx-auto"/>
      </div>
      <template v-else>
        <div class="modal-compact-content">
          <div class="table-responsive">
            <b-table
              class="card-table table-vcenter text-nowrap mb-0 custom-deal-table"
              tbody-tr-class="table-row"
              :bordered="false"
              :outlined="false"
              :hover="true"
              :items="dealList"
              :fields="getHeaders(true)"
              show-empty
              empty-text="No deal overrides added"
              sort-by="publisher"
              data-test="manage-deals-overrides-table"
            >
              <template #table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width ? field.width : 'auto' }"
                >
              </template>
              <template v-slot:cell(publisherId)="{item}">
                {{ getPublisherDetails(item.publisherId).name }}
              </template>
              <template v-slot:cell(creativeName)="{item}">
                {{ getCreative(item.creativeId).name }}
              </template>
              <template v-slot:cell(action)="{index}">
                <i
                  class="fal fa-trash-can float-right remove-btn"
                  @click="removeDeal(index)"
                  data-test="manage-deals-delete-button"
                />
              </template>
            </b-table>
          </div>

          <b-alert
            show
            variant="warning"
            v-if="showWarning"
            class="mt-2 mb-2"
          >
            Please note, all deals will be added when the campaign is created.
          </b-alert>
          <div class="form-label form-section-divider mb-0">
            <span class="form-section-title"> Add Deal Override </span>
          </div>

          <div class="table-responsive mb-2">
            <b-table
              class="card-table table-vcenter text-nowrap mb-0"
              tbody-tr-class="table-row"
              :bordered="false"
              :outlined="false"
              :items="newDealList"
              :fields="getHeaders()"
              sort-by="publisher"
            >
              <template #table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width ? field.width : 'auto' }"
                >
              </template>
              <template v-slot:cell(publisherId)="{item}">
                <b-form-select
                  v-model="item.publisherId"
                  :options="getPublisherList"
                  :disabled="isLoading"
                  data-test="manage-deals-publisher-select"
                />
              </template>
              <template v-slot:cell(creativeName)="{item}">
                <b-form-select
                  v-model="item.creativeId"
                  :options="getCreativesAsList"
                  :disabled="isLoading"
                  data-test="manage-deals-creative-id-select"
                />
              </template>
              <template v-slot:cell(dealIdString)="{item}">
                <input
                  type="text"
                  class="form-control"
                  v-model="item.dealIdString"
                  :disabled="isLoading"
                  data-test="manage-deals-deal-id-input"
                >
              </template>
            </b-table>
          </div>

          <div class="d-flex mt-2 mb-2">
            <b-btn
              class="ml-auto"
              size="sm"
              variant="secondary"
              @click="onUpdateDeals"
              :disabled="isAddButtonDisabled || isLoading"
              data-test="manage-deals-add-deal-button"
            >
              Add to list
            </b-btn>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapState,
  mapMutations,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as types from '@/store/types';

export default {
  name: 'ManageDeals',
  components: {},
  props: {
    publishers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      dealList: [],
      newDealList: [],
      dealSet: {
        publisherId: 0,
        creativeId: 0,
        dealIdString: '',
      },
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'dealOverrides',
      'publisherIds',
    ]),
    ...mapState('content', [
      'creative',
      'creatives',
    ]),
    ...mapGetters('campaigns', {
      isCampaignReadOnly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    getCreativesAsList() {
      return this.creatives.map((creative) => ({
        value: creative.id,
        text: creative.name,
      }));
    },
    isNewCampaign() {
      return (!this.campaign.id);
    },
    isListNotEmpty() {
      return (this.dealList.length !== 0);
    },
    showWarning() {
      return this.isNewCampaign && this.isListNotEmpty;
    },
    getPublisherList() {
      return this.publishers
        .filter((publisher) => this.publisherIds.includes(publisher.id))
        .map((publisher) => ({ value: publisher.id, text: publisher.name }));
    },
    isAddButtonDisabled() {
      if (!this.newDealList || this.newDealList.length === 0) return true;
      const newlyAddedDeal = this.newDealList[0];
      return !newlyAddedDeal.publisherId || !newlyAddedDeal.creativeId || !newlyAddedDeal.dealIdString || !newlyAddedDeal.dealIdString.trim();
    },
    hasUnsavedChangesInBottomPanel() {
      if (!this.newDealList || this.newDealList.length === 0) return false;
      const newlyAddedDeal = this.newDealList[0];
      return !!(newlyAddedDeal.publisherId || newlyAddedDeal.creativeId || (newlyAddedDeal.dealIdString && newlyAddedDeal.dealIdString.trim()));
    },
    hasDealListChanges() {
      const storeDeals = this.dealOverrides || [];
      if (this.dealList.length !== storeDeals.length) return true;

      return this.dealList.some((deal, index) => {
        const storeDeal = storeDeals[index];
        if (!storeDeal) return true;
        return (
          Number(deal.publisherId) !== Number(storeDeal.publisherId)
          || Number(deal.creativeId) !== Number(storeDeal.creativeId)
          || deal.dealIdString.trim() !== storeDeal.dealIdString.trim()
        );
      });
    },
  },
  methods: {
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_DEAL_OVERRIDES,
      campaignTypes.UPDATE_DEAL_OVERRIDES,
      campaignTypes.SUBMIT_DEAL_OVERRIDES,
    ]),
    getHeaders(actionCol) {
      const headerList = [
        {
          key: 'publisherId',
          label: 'Publisher',
          tdClass: 'text-wrap',
        },
        {
          key: 'creativeName',
          label: 'Creative',
          tdClass: 'text-wrap',
        },
        {
          key: 'dealIdString',
          label: 'Deal ID',
          width: '25%',
        },
      ];
      if (actionCol) {
        headerList.push({
          key: 'action',
          label: '',
          width: '5%',
        });
      }
      return headerList;
    },
    getCreative(id) {
      return this.creatives.find((creative) => creative.id === id);
    },
    addRow() {
      this.newDealList.push({ ...this.dealSet });
    },
    removeDeal(index) {
      this.dealList.splice(index, 1);
    },
    resetNewDeals() {
      this.newDealList = [];
      this.addRow();
    },
    showUnsavedChangesModal() {
      const h = this.$createElement;
      return this.$bvModal.msgBoxConfirm(h('p', [
        'You have unsaved changes!',
        h('br'),
        'Press "Proceed" to continue without saving, or "Cancel" to go back and review your changes',
      ]), {
        title: 'Warning',
        size: 'sm',
        buttonSize: 'sm',
        okTitle: 'Proceed',
        okVariant: 'danger',
        cancelTitle: 'Cancel',
        cancelVariant: 'tertiary',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      if (this.hasUnsavedChangesInBottomPanel) {
        this.showUnsavedChangesModal()
          .then((proceed) => {
            if (proceed) {
              this.dealList = this.dealOverrides;
              this.$bvModal.hide('manageDealsModal');
            }
          })
          .catch(() => {});
      } else {
        this.handleSubmit();
      }
    },
    handleHide(bvModalEvt) {
      if (bvModalEvt.trigger === 'ok') {
        return;
      }
      const hasUnsavedMainList = this.hasDealListChanges;
      const hasUnsavedBottomInput = this.hasUnsavedChangesInBottomPanel;

      if (hasUnsavedMainList || hasUnsavedBottomInput) {
        bvModalEvt.preventDefault();

        this.showUnsavedChangesModal()
          .then((confirmed) => {
            if (confirmed) {
              this.dealList = [...(this.dealOverrides || [])];
              this.resetNewDeals();
              this.$refs.manageDealsModal.hide();
            }
          })
          .catch(() => {});
      }
    },
    handleSubmit() {
      this.isLoading = true;

      const newDealList = this.dealList
        .map((deal) => ({
          publisherId: deal.publisherId,
          creativeId: deal.creativeId,
          dealIdString: deal.dealIdString.trim(),
        }));
      this.UPDATE_DEAL_OVERRIDES(newDealList);
      this.$nextTick(() => {
        this.SUBMIT_DEAL_OVERRIDES()
          .then(() => {
            this.SET_GLOBAL_MESSAGE({
              message: 'Manage deals updated successfully.',
              type: 'info',
            });
            this.$bvModal.hide('manageDealsModal');
          })
          .catch(() => {})
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    removeEmptyObjects(dealList) {
      return dealList.filter((o) => (o.publisherId && o.creativeId && o.dealIdString));
    },
    onUpdateDeals() {
      this.newDealList = this.removeEmptyObjects(this.newDealList);
      if (this.newDealList.length === 0) return this.addRow();
      this.dealList = [
        ...this.dealList,
        ...this.newDealList,
      ].slice()
        .reverse()
        .filter((deal, index, dealList) => {
          const uid = `${deal.publisherId}${deal.creativeId}`;
          return (dealList.findIndex((t) => (`${t.publisherId}${t.creativeId}` === uid)) === index);
        })
        .reverse();
      this.resetNewDeals();
    },
    getPublisherDetails(id) {
      return this.publishers.find((publisher) => Number(publisher.id) === Number(id));
    },
    fetchDealOverrides() {
      if (!this.isNewCampaign) {
        this.isLoading = true;
        this.FETCH_DEAL_OVERRIDES()
          .then(() => {
            this.dealList = this.dealOverrides.filter((deal) => this.publishers.some((publisher) => publisher.id === deal.publisherId));
            this.isLoading = false;
          });
      }
    },
  },
  mounted() {
    this.fetchDealOverrides();
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'manageDealsModal') {
        if (this.dealList.length === 0) this.dealList = this.dealOverrides;
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
  },
};
</script>

<style scoped>
.remove-btn {
  cursor: pointer;
  color: #a1a1a1;
  transition: color 0.15s ease;
}
.remove-btn:hover {
  color: #dc3545;
}

.modal-compact-content {
  display: block !important;
  height: auto !important;
  min-height: max-content !important;
}

.modal-compact-content .table-responsive {
  min-height: auto !important;
  height: auto !important;
}


.custom-form-table :deep(th),
.custom-form-table :deep(td) {
  border: none !important;
}
.form-section-divider {
  margin-top: 1.75rem;
  margin-bottom: 1rem;
  border-top: 2px dashed #e2e8f0;
  position: relative;
  text-align: left;
}

.form-section-title {
  position: relative;
  top: -11px;
  background: #ffffff;
  padding-right: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
}
</style>
